import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, Controller, useWatch } from 'react-hook-form';
import Select from 'react-select';
import Drawer from '../../Drawer';
import { inputStyles, labelStyles, requiredStyles } from './styles';
import { putCampaignRequestInitialContact } from '~/services/campaign';
import { apiUrl, authHeaders } from '~/services/auth';
import { createMedicalCenter } from '~/services/medical-centers';
import ReferringProfessionalField, {
    EMPTY_REFERRING_PROFESSIONAL,
    REFERRING_PROFESSIONAL_REFERRAL_CODES,
    ReferringProfessional,
    isReferringProfessionalMissing,
    referringProfessionalFromRequest,
    referringProfessionalPayload,
} from '../ReferringProfessionalField';

const OTHER_PROF_CODE = 'other_prof';
// Referral sources ("Referred By") for which the Medical Center picker is
// shown and its selection is saved. Matched on ReferredBySource.code.
const MEDICAL_CENTER_REFERRAL_CODES = [
    'med_center',
    'case_manager',
    'financial',
    'medical_professional',
    'social_worker',
    'tx_coord',
];

interface Props {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    campaignRequest: any;
    metadata: any;
    onUpdate?: (response: any) => void;
}

interface SelectOption {
    value: number | string;
    label: string;
}

const errorTextClass = 'mt-1 text-sm text-[#ff4b4b]';

const US_STATES: Record<string, string> = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District Of Columbia',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
};

// A medical center's location is a state's full name or 'Treatment Abroad'.
const stateOptions = [
    ...Object.entries(US_STATES).map(([value, label]) => ({
        value,
        label,
    })),
    { value: 'XX', label: 'Treatment Abroad' },
];

const searchMedicalCenters = async (state: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/v2/searchMedicalCenters`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ state }),
    });
    if (!resp.ok) throw new Error('Failed to search medical centers');
    return await resp.json();
};

const InitialContactInfoDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    campaignRequest,
    metadata,
    onUpdate,
}) => {
    const methods = useForm({
        defaultValues: {
            initial_first_name: '',
            initial_last_name: '',
            initial_email: '',
            initial_phone: '',
            request_source: '',
            campaign_is_for: null as any,
            heard_of_source: null as any,
            heard_of_source_other: '',
        },
    });

    const { reset, handleSubmit, control, formState, watch } = methods;
    const { errors } = formState;

    const watchedEmail = watch('initial_email');
    const watchedPhone = watch('initial_phone');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleClose = () => {
        if (
            formState.isDirty &&
            !window.confirm(
                'You have unsaved changes. Are you sure you want to close?',
            )
        ) {
            return;
        }
        setIsOpen(false);
    };

    // Medical center selection state
    const [selectedState, setSelectedState] = useState<SelectOption | null>(
        null,
    );
    const [selectedMedicalCenter, setSelectedMedicalCenter] =
        useState<SelectOption | null>(null);
    const [medicalCenterOptions, setMedicalCenterOptions] = useState<
        SelectOption[]
    >([]);
    const [loadingCenters, setLoadingCenters] = useState(false);
    const [showAddCenterForm, setShowAddCenterForm] = useState(false);
    const [newCenterName, setNewCenterName] = useState('');
    const [isCreatingCenter, setIsCreatingCenter] = useState(false);
    const [referringProfessional, setReferringProfessional] =
        useState<ReferringProfessional>(EMPTY_REFERRING_PROFESSIONAL);

    const requestSourceOptions = useMemo(
        () =>
            (metadata?.request_sources || []).map((option: any) => ({
                value: option?.value,
                label: option?.label,
            })),
        [metadata],
    );

    const relationshipOptions = useMemo(
        () =>
            (metadata?.campaign_is_for || [])
                .filter(
                    (item: any) => typeof item === 'object' && item !== null,
                )
                .map((item: any) => ({ value: item?.id, label: item?.name })),
        [metadata],
    );

    const heardOfOptions = useMemo(
        () =>
            (metadata?.heard_of_sources || []).map((item: any) => ({
                value: item?.id,
                label: item?.name,
                code: item?.code,
            })),
        [metadata],
    );

    // Watch heard_of_source to conditionally show fields
    const watchedHeardOfSource = useWatch({
        control,
        name: 'heard_of_source',
    });
    const isOtherSelected = watchedHeardOfSource?.code === OTHER_PROF_CODE;
    const isMedCenterSelected = MEDICAL_CENTER_REFERRAL_CODES.includes(
        watchedHeardOfSource?.code ?? '',
    );
    const isMedCenterMissing = isMedCenterSelected && !selectedMedicalCenter;
    const showMedCenterError = formState.isSubmitted && isMedCenterMissing;
    const isReferringSourceSelected =
        REFERRING_PROFESSIONAL_REFERRAL_CODES.includes(
            watchedHeardOfSource?.code ?? '',
        );
    const isReferrerMissing =
        isReferringSourceSelected &&
        isReferringProfessionalMissing(referringProfessional);
    const showReferrerError = formState.isSubmitted && isReferrerMissing;

    const loadMedicalCenters = async (
        state: string,
        preselectedMedCenterId?: number,
    ) => {
        setLoadingCenters(true);
        try {
            const centers = await searchMedicalCenters(state);
            const opts: SelectOption[] = centers.map((center: any) => ({
                label: center.name,
                value: center.id,
            }));
            setMedicalCenterOptions(opts);

            if (preselectedMedCenterId) {
                const selectedOpt = opts.find(
                    (opt) => opt.value === preselectedMedCenterId,
                );
                if (selectedOpt) {
                    setSelectedMedicalCenter(selectedOpt);
                }
            }
        } catch (error) {
            console.error('Error loading medical centers:', error);
        } finally {
            setLoadingCenters(false);
        }
    };

    // A picked person belongs to the old center; a typed name doesn't
    const clearPickedProfessional = () =>
        setReferringProfessional((prev) =>
            prev.isNew ? prev : EMPTY_REFERRING_PROFESSIONAL,
        );

    const handleStateChange = (option: SelectOption | null) => {
        setSelectedState(option);
        setSelectedMedicalCenter(null);
        clearPickedProfessional();
        setMedicalCenterOptions([]);
        setShowAddCenterForm(false);
        setNewCenterName('');

        if (option) {
            loadMedicalCenters(option.label);
        }
    };

    const handleMedicalCenterChange = (option: SelectOption | null) => {
        if (option?.value !== selectedMedicalCenter?.value) {
            clearPickedProfessional();
        }
        setSelectedMedicalCenter(option);
    };

    const handleAddCenter = async () => {
        if (!newCenterName.trim() || !selectedState) return;

        setIsCreatingCenter(true);
        try {
            const center = await createMedicalCenter({
                name: newCenterName.trim(),
                location: selectedState.label,
            });
            const newOption: SelectOption = {
                value: center.id,
                label: center.name,
            };
            setMedicalCenterOptions((prev) => [...prev, newOption]);
            setSelectedMedicalCenter(newOption);
            clearPickedProfessional();
            setShowAddCenterForm(false);
            setNewCenterName('');
        } catch (error) {
            console.error('Error creating medical center:', error);
        } finally {
            setIsCreatingCenter(false);
        }
    };

    // Reset medical center state when heard_of_source changes away from med_center
    useEffect(() => {
        if (!isMedCenterSelected) {
            setSelectedState(null);
            setSelectedMedicalCenter(null);
            setMedicalCenterOptions([]);
            setShowAddCenterForm(false);
            setNewCenterName('');
            setReferringProfessional(EMPTY_REFERRING_PROFESSIONAL);
        }
    }, [isMedCenterSelected]);

    useEffect(() => {
        if (!campaignRequest) return;

        reset({
            initial_first_name: campaignRequest?.initial_first_name || '',
            initial_last_name: campaignRequest?.initial_last_name || '',
            initial_email: campaignRequest?.initial_email || '',
            initial_phone: campaignRequest?.initial_phone || '',
            request_source: campaignRequest?.request_source || '',
            campaign_is_for: campaignRequest?.campaign_is_for
                ? {
                      value: campaignRequest.campaign_is_for.id,
                      label: campaignRequest.campaign_is_for.name,
                  }
                : null,
            heard_of_source: campaignRequest?.heard_of_source
                ? {
                      value: campaignRequest.heard_of_source.id,
                      label: campaignRequest.heard_of_source.name,
                      code: campaignRequest.heard_of_source.code,
                  }
                : null,
            heard_of_source_other: campaignRequest?.heard_of_source_other || '',
        });

        // Pre-populate medical center selection if referred by medical center.
        // Select the saved center even when its location isn't in the list,
        // since saving without one clears it from the request.
        if (
            MEDICAL_CENTER_REFERRAL_CODES.includes(
                campaignRequest?.heard_of_source?.code ?? '',
            ) &&
            campaignRequest?.medical_center
        ) {
            const medCenter = campaignRequest.medical_center;
            setSelectedMedicalCenter({
                value: medCenter.id,
                label: medCenter.name,
            });
            if (medCenter.location) {
                setSelectedState(
                    stateOptions.find(
                        (opt) => opt.label === medCenter.location,
                    ) ?? {
                        value: medCenter.location,
                        label: medCenter.location,
                    },
                );
                loadMedicalCenters(medCenter.location, medCenter.id);
            }
        } else {
            setSelectedState(null);
            setSelectedMedicalCenter(null);
            setMedicalCenterOptions([]);
        }
        setReferringProfessional(
            referringProfessionalFromRequest(campaignRequest),
        );
        setShowAddCenterForm(false);
        setNewCenterName('');
    }, [campaignRequest, reset, isOpen]);

    const onSubmit = async (values: any) => {
        if (isMedCenterMissing || isReferrerMissing) return;
        setIsSubmitting(true);
        try {
            const payload: any = {
                initial_first_name: values?.initial_first_name || '',
                initial_last_name: values?.initial_last_name || '',
                initial_email: values?.initial_email || '',
                initial_phone: values?.initial_phone || '',
                request_source:
                    values?.request_source ||
                    campaignRequest?.request_source ||
                    '',
                campaign_is_for_id: values?.campaign_is_for?.value ?? null,
                heard_of_source_id: values?.heard_of_source?.value ?? null,
                heard_of_source_other:
                    values?.heard_of_source?.code === OTHER_PROF_CODE
                        ? values?.heard_of_source_other || ''
                        : '',
            };

            // Include medical center if referred by medical center
            if (
                MEDICAL_CENTER_REFERRAL_CODES.includes(
                    values?.heard_of_source?.code ?? '',
                )
            ) {
                payload.medical_center_id =
                    selectedMedicalCenter?.value ?? null;
            }
            if (
                REFERRING_PROFESSIONAL_REFERRAL_CODES.includes(
                    values?.heard_of_source?.code ?? '',
                )
            ) {
                Object.assign(
                    payload,
                    referringProfessionalPayload(referringProfessional),
                );
            }

            const response = await putCampaignRequestInitialContact(
                campaignRequest?.id,
                payload,
            );

            onUpdate?.(response);
            setIsOpen(false);
        } catch (error) {
            console.error('Error updating initial contact information:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Drawer
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            onClose={handleClose}
            title='Initial Contact Info'
        >
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className='mb-5 flex gap-3'>
                        <div className='w-1/2'>
                            <label
                                htmlFor='icid-first-name'
                                className={`w-full ${labelStyles}`}
                            >
                                First Name
                                <span className={requiredStyles}>*</span>
                            </label>
                            <Controller
                                name='initial_first_name'
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <input
                                        id='icid-first-name'
                                        className={`w-full ${inputStyles}`}
                                        type='text'
                                        {...field}
                                    />
                                )}
                            />
                            {errors.initial_first_name && (
                                <p className={errorTextClass}>
                                    This field is required
                                </p>
                            )}
                        </div>
                        <div className='w-1/2'>
                            <label
                                htmlFor='icid-last-name'
                                className={`w-full ${labelStyles}`}
                            >
                                Last Name
                                <span className={requiredStyles}>*</span>
                            </label>
                            <Controller
                                name='initial_last_name'
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <input
                                        id='icid-last-name'
                                        className={`w-full ${inputStyles}`}
                                        type='text'
                                        {...field}
                                    />
                                )}
                            />
                            {errors.initial_last_name && (
                                <p className={errorTextClass}>
                                    This field is required
                                </p>
                            )}
                        </div>
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='icid-email'
                            className={`w-full ${labelStyles}`}
                        >
                            Email
                        </label>
                        <Controller
                            name='initial_email'
                            control={control}
                            rules={{
                                validate: (value) => {
                                    if (!value && !watchedPhone) {
                                        return 'Either email or phone is required';
                                    }
                                    return true;
                                },
                            }}
                            render={({ field }) => (
                                <input
                                    id='icid-email'
                                    className={`w-full ${inputStyles}`}
                                    type='email'
                                    {...field}
                                />
                            )}
                        />
                        {errors.initial_email && (
                            <p className={errorTextClass}>
                                {errors.initial_email.message ||
                                    'Either email or phone is required'}
                            </p>
                        )}
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='icid-phone'
                            className={`w-full ${labelStyles}`}
                        >
                            Contact Number
                        </label>
                        <Controller
                            name='initial_phone'
                            control={control}
                            rules={{
                                validate: (value) => {
                                    if (!value && !watchedEmail) {
                                        return 'Either email or phone is required';
                                    }
                                    if (
                                        value &&
                                        !/^\(\d{3}\) \d{3}-\d{4}$/.test(value)
                                    ) {
                                        return 'Please enter a valid phone number in format (123) 456-7890';
                                    }
                                    return true;
                                },
                            }}
                            render={({ field }) => (
                                <input
                                    id='icid-phone'
                                    className={`w-full ${inputStyles}`}
                                    type='tel'
                                    placeholder='(123) 456-7890'
                                    maxLength={14}
                                    onChange={(e) => {
                                        // Remove all non-digits
                                        const digits = e.target.value.replace(
                                            /\D/g,
                                            '',
                                        );

                                        // Format as (XXX) XXX-XXXX
                                        let formatted = '';
                                        if (digits.length >= 1) {
                                            formatted = `(${digits.slice(
                                                0,
                                                3,
                                            )}`;
                                        }
                                        if (digits.length >= 4) {
                                            formatted += `) ${digits.slice(
                                                3,
                                                6,
                                            )}`;
                                        }
                                        if (digits.length >= 7) {
                                            formatted += `-${digits.slice(
                                                6,
                                                10,
                                            )}`;
                                        }

                                        field.onChange(formatted);
                                    }}
                                    value={field.value}
                                />
                            )}
                        />
                        {errors.initial_phone && (
                            <p className={errorTextClass}>
                                {errors.initial_phone.message ||
                                    'Either email or phone is required'}
                            </p>
                        )}
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='icid-request-source'
                            className={`w-full ${labelStyles}`}
                        >
                            Request Source
                        </label>
                        <Controller
                            name='request_source'
                            control={control}
                            render={({ field }) => (
                                <Select
                                    id='icid-request-source'
                                    menuPlacement='auto'
                                    className='w-full'
                                    options={requestSourceOptions}
                                    value={
                                        requestSourceOptions.find(
                                            (option) =>
                                                option.value === field.value,
                                        ) || null
                                    }
                                    isClearable
                                    onChange={(option) =>
                                        field.onChange(option?.value || '')
                                    }
                                />
                            )}
                        />
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='icid-relationship'
                            className={`w-full ${labelStyles}`}
                        >
                            Relationship to Client
                        </label>
                        <Controller
                            name='campaign_is_for'
                            control={control}
                            render={({ field }) => (
                                <Select
                                    id='icid-relationship'
                                    menuPlacement='auto'
                                    className='w-full'
                                    options={relationshipOptions}
                                    isClearable
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='icid-heard-of'
                            className={`w-full ${labelStyles}`}
                        >
                            Referred By
                        </label>
                        <Controller
                            name='heard_of_source'
                            control={control}
                            render={({ field }) => (
                                <Select
                                    id='icid-heard-of'
                                    menuPlacement='auto'
                                    className='w-full'
                                    options={heardOfOptions}
                                    isClearable
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    {isOtherSelected && (
                        <div className='mb-5'>
                            <label
                                htmlFor='icid-heard-of-other'
                                className={`w-full ${labelStyles}`}
                            >
                                Please Specify
                            </label>
                            <Controller
                                name='heard_of_source_other'
                                control={control}
                                render={({ field }) => (
                                    <input
                                        id='icid-heard-of-other'
                                        className={`w-full ${inputStyles}`}
                                        type='text'
                                        placeholder='How did you hear about us?'
                                        {...field}
                                    />
                                )}
                            />
                        </div>
                    )}

                    {isMedCenterSelected && (
                        <>
                            <div className='mb-5'>
                                <label
                                    htmlFor='icid-med-center-state'
                                    className={`w-full ${labelStyles}`}
                                >
                                    Where is the Medical Center located?
                                    <span className={requiredStyles}>*</span>
                                </label>
                                <Select
                                    id='icid-med-center-state'
                                    menuPlacement='auto'
                                    options={stateOptions}
                                    value={selectedState}
                                    onChange={handleStateChange}
                                    placeholder='Select a state...'
                                    className='w-full'
                                    isDisabled={isSubmitting}
                                />
                                {showMedCenterError && !selectedState && (
                                    <p className={errorTextClass}>
                                        This field is required
                                    </p>
                                )}
                            </div>

                            {selectedState && !showAddCenterForm && (
                                <div className='mb-5'>
                                    <label
                                        htmlFor='icid-med-center'
                                        className={`w-full ${labelStyles}`}
                                    >
                                        Medical Center
                                        <span className={requiredStyles}>
                                            *
                                        </span>
                                    </label>
                                    <Select
                                        id='icid-med-center'
                                        menuPlacement='auto'
                                        options={medicalCenterOptions}
                                        value={selectedMedicalCenter}
                                        onChange={handleMedicalCenterChange}
                                        placeholder='Select a medical center...'
                                        className='w-full'
                                        isLoading={loadingCenters}
                                        isDisabled={
                                            isSubmitting || loadingCenters
                                        }
                                        isClearable
                                    />
                                    {showMedCenterError && (
                                        <p className={errorTextClass}>
                                            This field is required
                                        </p>
                                    )}
                                    <p className='mt-2 text-[14px] text-[#666]'>
                                        Is your Medical Center not listed here?{' '}
                                        <button
                                            type='button'
                                            className='text-[#0066CC] underline hover:text-[#0052A3]'
                                            onClick={() =>
                                                setShowAddCenterForm(true)
                                            }
                                        >
                                            Click here to add it.
                                        </button>
                                    </p>
                                </div>
                            )}

                            {isReferringSourceSelected &&
                                selectedMedicalCenter &&
                                !showAddCenterForm && (
                                    <ReferringProfessionalField
                                        id='icid-referring-professional'
                                        className='mb-5'
                                        medicalCenterId={
                                            selectedMedicalCenter.value as number
                                        }
                                        value={referringProfessional}
                                        onChange={setReferringProfessional}
                                        showError={showReferrerError}
                                        disabled={isSubmitting}
                                        labelClassName={`w-full ${labelStyles}`}
                                        inputClassName={`w-full ${inputStyles}`}
                                        requiredClassName={requiredStyles}
                                    />
                                )}

                            {showAddCenterForm && (
                                <div className='mb-5 border-l-4 border-[#FEBF3E] bg-[#FEF9EC] p-4'>
                                    <h4 className='mb-3 text-[14px] font-bold'>
                                        Add New Medical Center
                                    </h4>
                                    <label
                                        htmlFor='icid-new-center-name'
                                        className={`w-full ${labelStyles}`}
                                    >
                                        Medical Center Name
                                        <span className={requiredStyles}>
                                            *
                                        </span>
                                    </label>
                                    <input
                                        type='text'
                                        id='icid-new-center-name'
                                        className={`w-full ${inputStyles} mb-3`}
                                        value={newCenterName}
                                        onChange={(e) =>
                                            setNewCenterName(e.target.value)
                                        }
                                        disabled={isCreatingCenter}
                                        placeholder='Enter medical center name'
                                    />
                                    <div className='flex gap-3'>
                                        <button
                                            type='button'
                                            className='rounded-full border-[2px] px-4 py-1 text-[13px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                                            onClick={() => {
                                                setShowAddCenterForm(false);
                                                setNewCenterName('');
                                            }}
                                            disabled={isCreatingCenter}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type='button'
                                            className='rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-4 py-1 text-[13px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8] disabled:cursor-not-allowed disabled:opacity-50'
                                            onClick={handleAddCenter}
                                            disabled={
                                                isCreatingCenter ||
                                                !newCenterName.trim()
                                            }
                                        >
                                            {isCreatingCenter
                                                ? 'Adding...'
                                                : 'Add'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    <div className='mb-[30px] flex gap-[16px]'>
                        <button
                            type='button'
                            className='mt-[30px] grow rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                            onClick={handleClose}
                        >
                            Cancel
                        </button>
                        <button
                            type='submit'
                            className='mt-[30px] grow rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8]'
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </FormProvider>
        </Drawer>
    );
};

export default InitialContactInfoDrawer;
